/**
 * Vault-equivalent values for Atlas typography primitives.
 *
 * The semantic roles (headingH1, body, etc.) are wired in
 * `vaultTypographyOverrides.ts`. This file overrides the underlying primitive
 * tokens (titleH1, textBody, etc.) so any component reading them directly
 * (rare but possible) also gets Vault values.
 *
 * Same strategy: keep px sizes identical to Atlas to prevent layout shifts,
 * swap font-family from Plus Jakarta Sans → Rubik.
 */

import type {
  AtlasTypographyPrimitives,
  AtlasTypographyToken,
} from '../typography';

const vaultHeadingFontFamily = '"Rubik", sans-serif';
const vaultBodyFontFamily = '"Ubuntu", sans-serif';

export const vaultTypographyPrimitiveOverrides = {
  titleH1: {
    fontFamily: vaultHeadingFontFamily,
    fontWeight: 600,
    fontSize: 30,
    lineHeight: 38,
    letterSpacing: 0,
  },
  titleH2: {
    fontFamily: vaultHeadingFontFamily,
    fontWeight: 600,
    fontSize: 26,
    lineHeight: 34,
    letterSpacing: 0,
  },
  titleH3: {
    fontFamily: vaultHeadingFontFamily,
    fontWeight: 600,
    fontSize: 22,
    lineHeight: 28,
    letterSpacing: 0,
  },
  titleH4: {
    fontFamily: vaultHeadingFontFamily,
    fontWeight: 600,
    fontSize: 18,
    lineHeight: 28,
    letterSpacing: 0.2,
  },
  textBody: {
    fontFamily: vaultBodyFontFamily,
    fontWeight: 400,
    fontSize: 16,
    lineHeight: 24,
    letterSpacing: 0,
  },
  textMd: {
    fontFamily: vaultBodyFontFamily,
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 24,
    letterSpacing: 0,
  },
  textSm: {
    fontFamily: vaultBodyFontFamily,
    fontWeight: 400,
    fontSize: 12,
    lineHeight: 20,
    letterSpacing: 0,
  },
  emphasis: {
    fontFamily: vaultBodyFontFamily,
    fontWeight: 500,
    fontSize: 16,
    lineHeight: 24,
    letterSpacing: 0.2,
  },
  button: {
    fontFamily: vaultBodyFontFamily,
    fontWeight: 500,
    fontSize: 16,
    lineHeight: 25.5,
    letterSpacing: 0.01,
  },
} satisfies Record<keyof AtlasTypographyPrimitives, AtlasTypographyToken>;
